import { catchError } from 'rxjs/operators';
import { ActionFunction1 } from 'redux-actions';
import { ErrorActionPayload } from 'types/types';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { getCurrentConnectionStatus } from 'utils/connectionStatus';
import { extractApiErrorMessage } from 'utils/epics/extractApiErrorMessage';

/** Stable toast ID so multiple concurrent network errors produce ONE toast. */
const NETWORK_ERROR_TOAST_ID = 'network-error';

/**
 * Check whether an Axios error is a network-level failure (device offline,
 * DNS unreachable, etc.).
 */
const isNetworkError = (err: AxiosError): boolean =>
  !err.response && err.message === 'Network Error';

export const handleAjaxError = (
  failActionCreator: ActionFunction1<any, any>,
  customErrorMessage?: string,
  toastId?: string,
) =>
  catchError((err: AxiosError) => {
    // Prefer the server-provided message over any caller-supplied fallback
    // or the generic Axios wrapper (e.g. "Request failed with status code
    // 500"), so users see the real reason for the failure.
    const backendMessage = extractApiErrorMessage(err);
    const finalMessage = backendMessage || customErrorMessage || err?.message;

    const errorActionPayload: ErrorActionPayload = {
      message: finalMessage,
      name: err?.name,
      status: err?.response?.status,
    };
    if (toastId) toast.dismiss(toastId);

    if (isNetworkError(err)) {
      // When the browser already knows we're offline the connectionStatus
      // module shows a persistent "Connection is lost or unstable" toast,
      // so we don't need to show anything extra.
      // When the browser thinks we're online but the server is unreachable,
      // show a single deduplicated toast (fixed ID prevents stacking).
      if (getCurrentConnectionStatus() === 'online') {
        toast.error('Connection is lost or unstable', {
          id: NETWORK_ERROR_TOAST_ID,
        });
      }
    } else if (
      (!err.response || (err?.response && err?.response?.status !== 401)) &&
      finalMessage
    ) {
      toast.error(finalMessage);
    }

    return [failActionCreator(errorActionPayload)];
  });
